import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';
import {
    BILLBOARD_DESKTOP_WIDTH,
    BILLBOARD_DESKTOP_HEIGHT,
    BILLBOARD_TABLET_WIDTH,
    BILLBOARD_TABLET_HEIGHT,
    BILLBOARD_MOBILE_WIDTH,
    BILLBOARD_MOBILE_HEIGHT,
    ELEMENT_ID_BILLBOARD,
    ELEMENT_ID_BILLBOARD_MOBILE,
    ELEMENT_ID_BILLBOARD_TABLET,
    ELEMENT_ID_PRODUCT_TILE_AD_PREFIX,
    PRODUCT_TILE_HEIGHT,
    PRODUCT_TILE_WIDTH,
    TILE_AD_KEY,
    HOME_TOP_BILLBOARD_PLACEMENT,
    HOME_BOTTOM_BILLBOARD_PLACEMENT,
    ORDER_CONFIRMATION_BILLBOARD_PLACEMENT,
    PDP_AVAILABLE_TOP_BILLBOARD_PLACEMENT,
    PDP_AVAILABLE_BOTTOM_BILLBOARD_PLACEMENT,
    PDP_CONDENSED_BOTTOM_BILLBOARD_PLACEMENT,
    PDP_CONDENSED_TOP_BILLBOARD_PLACEMENT,
    PDP_SOLD_BOTTOM_BILLBOARD_PLACEMENT,
    PDP_SOLD_TOP_BILLBOARD_PLACEMENT,
    BILLBOARD_TABLET_HEIGHT_V2,
    BILLBOARD_TABLET_WIDTH_V2,
    BILLBOARD_MOBILE_HEIGHT_V2,
    PRODUCT_TILE_WIDTH_V2,
    PRODUCT_TILE_HEIGHT_V2,
    MAX_PRODUCT_TILE_ADS,
    AD_UNIT_NETWORK_CODE,
} from '../constants';
import {
    type AdData,
    type Ad,
    type AdProductTile,
    type AdProductTileV2,
    type AdSizes,
    type BillboardConfig,
} from '../types';

const calculateAdHolds = (sizes: AdSizes): { heightHold?: number; widthHold?: number } =>
    sizes.reduce<{ heightHold?: number; widthHold?: number }>((acc, [width, height]) => {
        acc.heightHold = Math.max(acc.heightHold ?? 0, height);
        acc.widthHold = Math.max(acc.widthHold ?? 0, width);
        return acc;
    }, {});

const createProductTileData = (
    productTileData?: Ad[],
    useV2Sizes?: boolean,
    productTileSizes?: AdSizes
): AdData['productTile'] => {
    let productTile: AdData['productTile'] = [];
    const defaultSizes: AdSizes = useV2Sizes
        ? [[PRODUCT_TILE_WIDTH_V2, PRODUCT_TILE_HEIGHT_V2]]
        : [[PRODUCT_TILE_WIDTH, PRODUCT_TILE_HEIGHT]];

    const sizes = productTileSizes || defaultSizes;
    const { heightHold, widthHold } = calculateAdHolds(sizes);

    if (Array.isArray(productTileData)) {
        productTile = productTileData.map((tile, index) => {
            return {
                adId: tile.adId,
                elementId: `${ELEMENT_ID_PRODUCT_TILE_AD_PREFIX}-${index}`,
                width: useV2Sizes ? PRODUCT_TILE_WIDTH_V2 : PRODUCT_TILE_WIDTH,
                height: useV2Sizes ? PRODUCT_TILE_HEIGHT_V2 : PRODUCT_TILE_HEIGHT,
                sizes,
                heightHold:
                    heightHold || (useV2Sizes ? PRODUCT_TILE_HEIGHT_V2 : PRODUCT_TILE_HEIGHT),
                widthHold: widthHold || (useV2Sizes ? PRODUCT_TILE_WIDTH_V2 : PRODUCT_TILE_WIDTH),
            };
        });
    }
    return productTile;
};

const createProductTileV2 = ({
    adUnitCodePart,
    adIndex,
    sizes,
    heightHold,
    widthHold,
}: {
    adUnitCodePart: string;
    adIndex: number;
    sizes: AdSizes;
    heightHold: number;
    widthHold: number;
}): AdProductTile => ({
    adId: `/${AD_UNIT_NETWORK_CODE}/${adUnitCodePart}_${adIndex}`,
    elementId: `${ELEMENT_ID_PRODUCT_TILE_AD_PREFIX}-${adIndex}`,
    sizes,
    heightHold,
    widthHold,
    // Leaving these here for backwards type compatibility, to remove after we migrate to v2 setup
    width: PRODUCT_TILE_WIDTH_V2,
    height: PRODUCT_TILE_HEIGHT_V2,
});

const createProductTileDataV2 = ({
    adUnitCodeBase,
    numberOfTiles = MAX_PRODUCT_TILE_ADS,
    productTileSizes,
}: {
    productTileSizes?: AdSizes;
    adUnitCodeBase: string;
    numberOfTiles?: number;
}): AdProductTileV2 => {
    const sizes = productTileSizes || [[PRODUCT_TILE_WIDTH_V2, PRODUCT_TILE_HEIGHT_V2]];

    const { heightHold = PRODUCT_TILE_HEIGHT_V2, widthHold = PRODUCT_TILE_WIDTH_V2 } =
        calculateAdHolds(sizes);

    return {
        desktop: Array.from({ length: numberOfTiles }, (_, index) =>
            createProductTileV2({
                adUnitCodePart: `${adUnitCodeBase}_desktop`,
                adIndex: index + 1,
                sizes,
                heightHold,
                widthHold,
            })
        ),
        mobile: Array.from({ length: numberOfTiles }, (_, index) =>
            createProductTileV2({
                adUnitCodePart: `${adUnitCodeBase}_mweb`,
                adIndex: index + 1,
                sizes,
                heightHold,
                widthHold,
            })
        ),
    };
};

const createBillboardData = (
    billboardData?: {
        desktop?: Ad;
        mobile?: Ad;
        tablet?: Ad;
    },
    useV2Sizes?: boolean
): AdData['billboard'] => {
    const { desktop, tablet, mobile } = billboardData || {};

    const data: AdData['billboard'] = {};

    if (desktop) {
        data.desktop = {
            adId: desktop.adId,
            elementId: `${ELEMENT_ID_BILLBOARD}-${desktop.adId}`,
            width: BILLBOARD_DESKTOP_WIDTH,
            height: BILLBOARD_DESKTOP_HEIGHT,
        };
    }

    if (tablet) {
        data.tablet = {
            adId: tablet.adId,
            elementId: `${ELEMENT_ID_BILLBOARD_TABLET}-${tablet.adId}`,
            width: useV2Sizes ? BILLBOARD_TABLET_WIDTH_V2 : BILLBOARD_TABLET_WIDTH,
            height: useV2Sizes ? BILLBOARD_TABLET_HEIGHT_V2 : BILLBOARD_TABLET_HEIGHT,
        };
    }

    if (mobile) {
        data.mobile = {
            adId: mobile.adId,
            elementId: `${ELEMENT_ID_BILLBOARD_MOBILE}-${mobile.adId}`,
            width: BILLBOARD_MOBILE_WIDTH,
            height: useV2Sizes ? BILLBOARD_MOBILE_HEIGHT_V2 : BILLBOARD_MOBILE_HEIGHT,
            sizes: [
                [BILLBOARD_MOBILE_WIDTH, BILLBOARD_MOBILE_HEIGHT],
                [BILLBOARD_MOBILE_WIDTH, BILLBOARD_MOBILE_HEIGHT_V2],
            ],
            heightHold: BILLBOARD_MOBILE_HEIGHT_V2,
            widthHold: BILLBOARD_MOBILE_WIDTH,
        };
    }

    return data;
};

const createBillboardDataV2 = ({
    adUnitCodeBase,
    billboardConfig,
}: {
    adUnitCodeBase: string;
    billboardConfig?: BillboardConfig;
}): AdData['billboard'] => {
    return Object.entries(billboardConfig || {}).reduce((acc, [adDeviceType, config]) => {
        const isDesktopAd = adDeviceType === 'desktop';
        const adId = `/${AD_UNIT_NETWORK_CODE}/${adUnitCodeBase}_${adDeviceType}`;
        let { heightHold, widthHold } = config.sizes.reduce<{
            heightHold?: number;
            widthHold?: number;
        }>((holds, [width, height]) => {
            holds.heightHold = Math.max(holds.heightHold ?? 0, height);
            holds.widthHold = Math.max(holds.widthHold ?? 0, width);
            return holds;
        }, {});

        const height = isDesktopAd ? BILLBOARD_DESKTOP_HEIGHT : BILLBOARD_MOBILE_HEIGHT_V2;
        const width = isDesktopAd ? BILLBOARD_DESKTOP_WIDTH : BILLBOARD_MOBILE_WIDTH;
        heightHold = heightHold || height;
        widthHold = widthHold || width;

        return {
            ...acc,
            [adDeviceType]: {
                adId,
                elementId: `${ELEMENT_ID_BILLBOARD}_${adId}`,
                sizes: config.sizes,
                heightHold,
                widthHold,
                width,
                height,
            },
        };
    }, {});
};

// only exporting for testing
/**
 * This is oldest version of generateAdData (deprecated)
 */
export function generateAdData({}: {
    productTileData?: Ad[];
    billboardData?: { desktop?: Ad; mobile?: Ad; tablet?: Ad };
    billboardConfig?: never;
    useV2Sizes?: boolean;
    adUnitCodeBase?: never;
    productTileSizes?: AdSizes;
}): {
    billboard: AdData['billboard'];
    productTile: AdProductTile[];
};
/**
 * This is the version of generateAdData with adUnitCodeBase for product tiles only
 */
export function generateAdData({}: {
    productTileData?: never;
    billboardData?: { desktop?: Ad; mobile?: Ad; tablet?: Ad };
    billboardConfig?: never;
    useV2Sizes?: boolean;
    adUnitCodeBase: string;
    productTileSizes?: AdSizes;
}): {
    billboard: AdData['billboard'];
    productTile: AdProductTileV2;
};
/**
 * This is the version of generateAdData with adUnitCodeBase for both product tiles and billboards
 */
export function generateAdData({}: {
    productTileData?: never;
    billboardData?: never;
    useV2Sizes?: boolean;
    adUnitCodeBase: string;
    productTileSizes?: AdSizes;
    billboardConfig?: BillboardConfig;
}): {
    billboard: AdData['billboard'];
    productTile: AdProductTileV2;
};
export function generateAdData({
    productTileData,
    billboardData,
    useV2Sizes = false,
    adUnitCodeBase,
    productTileSizes,
    billboardConfig,
}: {
    productTileData?: Ad[];
    billboardData?: { desktop?: Ad; mobile?: Ad; tablet?: Ad };
    useV2Sizes?: boolean;
    adUnitCodeBase?: string;
    productTileSizes?: AdSizes;
    billboardConfig?: BillboardConfig;
}): AdData {
    return adUnitCodeBase
        ? {
              productTile: createProductTileDataV2({ adUnitCodeBase, productTileSizes }),
              billboard: billboardConfig
                  ? createBillboardDataV2({ adUnitCodeBase, billboardConfig })
                  : createBillboardData(billboardData, useV2Sizes),
          }
        : {
              productTile: createProductTileData(productTileData, useV2Sizes, productTileSizes),
              billboard: createBillboardData(billboardData, useV2Sizes),
          };
}

const productTileSizes: AdSizes = [
    [331, 419],
    [300, 250],
];

const AD_JEWELRY = generateAdData({
    adUnitCodeBase: 'jewelry_featured_item',
    productTileSizes,
    billboardData: {
        desktop: {
            adId: '/5391766/Jewelry_BottomBillboardBanner',
        },
        tablet: {
            adId: '/5391766/Small_Jewelry_BillboardBanner',
        },
        mobile: {
            adId: '/5391766/Jewelry_BillboardBanner_Mobile',
        },
    },
});

const AD_FASHION = generateAdData({
    adUnitCodeBase: 'fashion_featured_item',
    productTileSizes,
    billboardData: {
        desktop: {
            adId: '/5391766/Fashion_BillboardBanner',
        },
        tablet: {
            adId: '/5391766/Small_Fashion_BillboardBanner',
        },
        mobile: {
            adId: '/5391766/Fashion_BillboardBanner_Mobile',
        },
    },
});

const AD_FURNITURE = generateAdData({
    adUnitCodeBase: 'furniture_featured_item',
    productTileSizes,
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner',
        },
        tablet: {
            adId: '/5391766/Small_Furniture_BillboardBanner',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Mobile',
        },
    },
});

const AD_ART = generateAdData({
    adUnitCodeBase: 'art_featured_item',
    productTileSizes,
    billboardData: {
        desktop: {
            adId: '/5391766/FineArt_BottomBillboard',
        },
        tablet: {
            adId: '/5391766/Small_FineArt_BillboardBanner',
        },
        mobile: {
            adId: '/5391766/FineArt_BillboardBanner_Mobile',
        },
    },
});

const AD_SATURDAY_SALE = generateAdData({
    adUnitCodeBase: 'sale_featured_item',
    productTileSizes,
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Sale',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Sale',
        },
    },
});

const AD_DEFAULT = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner',
        },
    },
});

const AD_CATALOGUE_COLLECTION = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Catalogue',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Catalogue_Mobile',
        },
    },
});

const AD_HOLIDAY_COLLECTION = generateAdData({
    productTileData: [
        { adId: '/5391766/Featured_Item_Banner_Holiday_Collection_2020_1' },
        { adId: '/5391766/Featured_Item_Banner_Holiday_Collection_2020_2' },
        { adId: '/5391766/Featured_Item_Banner_Holiday_Collection_2020_3' },
        { adId: '/5391766/Featured_Item_Banner_Holiday_Collection_2020_4' },
    ],
    productTileSizes,
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Holiday_2020',
        },
        tablet: {
            adId: '/5391766/Bottom_Billboard_Banner_Holiday_2020_Tablet',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Holiday_2020_Mobile',
        },
    },
});

const AD_HOLIDAY_SUB_COLLECTION = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Holiday_2020_SubGiftGuides',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Holiday_2020_SubGiftGuides_Mobile',
        },
    },
});

const AD_VALENTINES_COLLECTION = generateAdData({
    productTileData: [
        { adId: '/5391766/Featured_Item_Banner_Valentine_1' },
        { adId: '/5391766/Featured_Item_Banner_Valentine_2' },
        { adId: '/5391766/Featured_Item_Banner_Valentine_3' },
        { adId: '/5391766/Featured_Item_Banner_Valentine_4' },
    ],
    productTileSizes,
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Valentine',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Valentine_Mobile',
        },
    },
});

const AD_ENGAGEMENT_RINGS_COLLECTION = generateAdData({
    productTileData: [
        { adId: '/5391766/Featured_Item_Banner_Engagement_Rings_1' },
        { adId: '/5391766/Featured_Item_Banner_Engagement_Rings_2' },
        { adId: '/5391766/Featured_Item_Banner_Engagement_Rings_3' },
        { adId: '/5391766/Featured_Item_Banner_Engagement_Rings_4' },
    ],
    productTileSizes,
});

const AD_MOTHERS_DAY_COLLECTION = generateAdData({
    productTileData: [
        { adId: '/5391766/Featured_Item_Banner_Mothers_Day_1' },
        { adId: '/5391766/Featured_Item_Banner_Mothers_Day_2' },
        { adId: '/5391766/Featured_Item_Banner_Mothers_Day_3' },
        { adId: '/5391766/Featured_Item_Banner_Mothers_Day_4' },
    ],
    productTileSizes,
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Mothers_Day',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Mothers_Day_Mobile',
        },
    },
});

const AD_FATHERS_DAY_COLLECTION = generateAdData({
    productTileData: [
        { adId: '/5391766/Featured_Item_Banner_Fathers_Day_1' },
        { adId: '/5391766/Featured_Item_Banner_Fathers_Day_2' },
        { adId: '/5391766/Featured_Item_Banner_Fathers_Day_3' },
        { adId: '/5391766/Featured_Item_Banner_Fathers_Day_4' },
    ],
    productTileSizes,
});

const AD_SPRING_CATALOGUE = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Spring_Catalogue',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Spring_Catalogue_Mobile',
        },
    },
});

const AD_FALL_CATALOGUE = generateAdData({
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_Fall_Catalogue',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_Fall_Catalogue_Mobile',
        },
    },
});

const AD_NEW_ARRIVALS = generateAdData({
    adUnitCodeBase: 'new_arrivals_featured_item',
    productTileSizes,
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner_New_Arrivals',
        },
        mobile: {
            adId: '/5391766/Bottom_Billboard_Banner_New_Arrivals_Mobile',
        },
    },
});

const homepageBillboardConfig: BillboardConfig = {
    desktop: {
        sizes: [
            [970, 250],
            [970, 90],
            [728, 90],
        ],
    },
    mobile: {
        sizes: [
            [320, 100],
            [320, 50],
        ],
    },
};

const AD_HOMEPAGE_TOP = generateAdData({
    adUnitCodeBase: 'homepage_top_leaderboard',
    billboardConfig: homepageBillboardConfig,
});

const AD_HOMEPAGE_BOTTOM = generateAdData({
    adUnitCodeBase: 'homepage_bottom_leaderboard',
    billboardConfig: homepageBillboardConfig,
});

const AD_CONFIRMATION = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/confirmation_page',
        },
        mobile: {
            adId: '/5391766/confirmation_page',
        },
    },
    useV2Sizes: true,
});

const AD_BUY_PAGE = generateAdData({
    productTileData: [
        { adId: '/5391766/buy_grid_1' },
        { adId: '/5391766/buy_grid_2' },
        { adId: '/5391766/buy_grid_3' },
        { adId: '/5391766/buy_grid_4' },
    ],
    productTileSizes,
    billboardData: {
        tablet: {
            adId: '/5391766/buy_bottom',
        },
        mobile: {
            adId: '/5391766/buy_bottom',
        },
    },
    useV2Sizes: true,
});

const AD_PDP_AVAILABLE_TOP = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/pdp_available',
        },
        mobile: {
            adId: '/5391766/pdp_available',
        },
    },
    useV2Sizes: true,
});

const AD_PDP_AVAILABLE_BOTTOM = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/pdp_available_bottom',
        },
        mobile: {
            adId: '/5391766/pdp_available_bottom',
        },
    },
    useV2Sizes: true,
});

const AD_PDP_CONDENSED_TOP = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/pdp_condensed',
        },
        mobile: {
            adId: '/5391766/pdp_condensed',
        },
    },
    useV2Sizes: true,
});

const AD_PDP_CONDENSED_BOTTOM = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/pdp_condensed_bottom',
        },
        mobile: {
            adId: '/5391766/pdp_condensed_bottom',
        },
    },
    useV2Sizes: true,
});

const AD_PDP_SOLD_TOP = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/pdp_sold',
        },
        mobile: {
            adId: '/5391766/pdp_sold',
        },
    },
    useV2Sizes: true,
});

const AD_PDP_SOLD_BOTTOM = generateAdData({
    billboardData: {
        tablet: {
            adId: '/5391766/pdp_sold_bottom',
        },
        mobile: {
            adId: '/5391766/pdp_sold_bottom',
        },
    },
    useV2Sizes: true,
});

const AD_SEARCH = generateAdData({
    adUnitCodeBase: 'search_box_featured_item',
    productTileSizes,
    billboardData: {
        desktop: {
            adId: '/5391766/Bottom_Billboard_Banner',
        },
    },
});

export const googleAdMap: Record<string, AdData> = {
    jewelry: AD_JEWELRY,
    fashion: AD_FASHION,
    furniture: AD_FURNITURE,
    art: AD_ART,
    contemporary: AD_FURNITURE,
    'saturday-sale': AD_SATURDAY_SALE,
    'design-gestalt': AD_CATALOGUE_COLLECTION,
    'holiday-gift-guide': AD_HOLIDAY_COLLECTION,
    'art-gifts': AD_HOLIDAY_SUB_COLLECTION,
    'jewelry-gifts': AD_HOLIDAY_SUB_COLLECTION,
    'rare-gifts': AD_HOLIDAY_SUB_COLLECTION,
    'iconic-gifts': AD_HOLIDAY_SUB_COLLECTION,
    'valentines-gift-guide': AD_VALENTINES_COLLECTION,
    'exquisite-engagement-rings': AD_ENGAGEMENT_RINGS_COLLECTION,
    'mothers-day-collection': AD_MOTHERS_DAY_COLLECTION,
    'fathers-day-collection': AD_FATHERS_DAY_COLLECTION,
    'spring-catalogue': AD_SPRING_CATALOGUE,
    'fall-catalogue': AD_FALL_CATALOGUE,
    'new-arrivals': AD_NEW_ARRIVALS,
    buy: AD_BUY_PAGE,
    search: AD_SEARCH,
    // Below are not uriRef segments, but placement keys
    [HOME_TOP_BILLBOARD_PLACEMENT]: AD_HOMEPAGE_TOP,
    [HOME_BOTTOM_BILLBOARD_PLACEMENT]: AD_HOMEPAGE_BOTTOM,
    [ORDER_CONFIRMATION_BILLBOARD_PLACEMENT]: AD_CONFIRMATION,
    [PDP_AVAILABLE_TOP_BILLBOARD_PLACEMENT]: AD_PDP_AVAILABLE_TOP,
    [PDP_AVAILABLE_BOTTOM_BILLBOARD_PLACEMENT]: AD_PDP_AVAILABLE_BOTTOM,
    [PDP_CONDENSED_TOP_BILLBOARD_PLACEMENT]: AD_PDP_CONDENSED_TOP,
    [PDP_CONDENSED_BOTTOM_BILLBOARD_PLACEMENT]: AD_PDP_CONDENSED_BOTTOM,
    [PDP_SOLD_TOP_BILLBOARD_PLACEMENT]: AD_PDP_SOLD_TOP,
    [PDP_SOLD_BOTTOM_BILLBOARD_PLACEMENT]: AD_PDP_SOLD_BOTTOM,
    default: AD_DEFAULT,
} as const;

export const sbAdExcludeMap: Record<string, readonly string[]> = {
    [PAGE_TYPE.MORE_FROM_SELLER]: [TILE_AD_KEY],
    [PAGE_TYPE.DEALERS]: [TILE_AD_KEY],
} as const;

const AD_TRADE = generateAdData({
    adUnitCodeBase: 'trade_featured_item',
    productTileSizes,
});

export const tradeGoogleAdMap: Record<string, AdData> = {
    furniture: AD_TRADE,
    art: AD_TRADE,
    jewelry: AD_TRADE,
    fashion: AD_TRADE,
};
